import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { productApi } from '@/api/commercial/products';
import { deviceApi } from '@/api/production/devices';
import { toast } from '@/hooks/use-toast';
import { type CreateDeviceFormData, createDeviceSchema } from '@/schemas/production/device.schema';
import type { CreateDeviceDto } from '@/types/production/device';
import { mapProductNameToDeviceModel } from '@/utils/device-model-mapper';

export function useDeviceCreateForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => productApi.findMany({ filter: {}, limit: 100 }),
  });

  const form = useForm<CreateDeviceFormData>({
    resolver: zodResolver(createDeviceSchema),
  });

  const mutation = useMutation({
    mutationFn: deviceApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast({
        title: 'Sucesso!',
        description: 'Equipamento registrado com sucesso!',
      });
      navigate('/production/devices');
    },
    onError: () => {
      toast({
        title: 'Erro!',
        description: 'Falha ao registrar o equipamento!',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const products = productsData?.docs || [];
    const product = products.find((p) => p.id === data.product_id);

    if (!product) {
      toast({
        title: 'Erro!',
        description: 'Produto não encontrado!',
        variant: 'destructive',
      });
      return;
    }

    const deviceModel = mapProductNameToDeviceModel(product.name);

    if (!deviceModel) {
      toast({
        title: 'Erro!',
        description: `Produto "${product.name}" não possui mapeamento para modelo de dispositivo!`,
        variant: 'destructive',
      });
      return;
    }

    const deviceData: CreateDeviceDto = {
      equipment: {
        serial: data.equipment.serial,
        firmware: 'N/A', // Default firmware value
      },
      model: deviceModel as any,
    };

    mutation.mutate(deviceData);
  });

  return {
    form,
    handleSubmit,
    isSubmitting: mutation.isPending,
    products: productsData?.docs || [],
  };
}
